import type { PlanTermProps, PlanTermWorkingTimeProps } from '../context';
import moment from 'moment';
import { PRESENT_REGEX } from '../../variables';
import { getDaysDuration } from '../utils';

type validateDatesProps = Pick<PlanTermProps, 'startDate' | 'endDate'>;

export const validateDates = (data: validateDatesProps): boolean => {
  const startDate = moment(data.startDate);
  const endDate = moment(data.endDate);
  return (
    startDate.isValid() &&
    endDate.isValid() &&
    endDate.isSameOrAfter(startDate, 'day')
  );
};

type validateNumberProps = Pick<PlanTermProps, 'number'>;

export const validateNumber = (data: validateNumberProps): boolean =>
  data.number > 0;

export const validateString = (
  value: string,
  required: boolean = false,
): boolean =>
  (!required && ['', null, undefined].includes(value)) ||
  (!!value && PRESENT_REGEX.test(value));

export const validateTime = (
  value: moment.MomentInput,
  required: boolean = false,
): boolean =>
  (!required && [null, undefined].includes(value)) ||
  (!!value && moment(value).isValid());

export const validateBoolean = (
  value: boolean | number | null,
  required: boolean = false,
): boolean =>
  (!required && value == null) || [true, false, 1, 0].includes(value);

type validateHolidayProps = Pick<
  PlanTermProps,
  'startDate' | 'endDate' | 'holidays',
>;

export const validateHoliday = (data: validateHolidayProps): boolean => {
  const startDate = moment(data.startDate);
  const endDate = moment(data.endDate);
  const isMinHolidaysOrOver = data.holidays >= data.minHolidays;

  return (
    data.holidays >= 0 &&
    getDaysDuration({ startDate, endDate, includeSameDay: true }) >=
      data.holidays &&
    isMinHolidaysOrOver
  );
};

type validateFirstDayStatusProps = Pick<PlanTermProps, 'firstDayStatus'>;

export const validateFirstDayStatus = (
  data: validateFirstDayStatusProps,
): boolean => ['scheduled', 'moving'].includes(data.firstDayStatus);

type validateLastDayStatusProps = Pick<PlanTermProps, 'lastDayStatus'>;

export const validateLastDayStatus = (
  data: validateLastDayStatusProps,
): boolean => ['scheduled', 'moving'].includes(data.lastDayStatus);

export const validateDayTime = (value: moment.MomentInput): boolean => {
  const time = moment(value);
  return (
    value != null &&
    time.isValid() &&
    moment(new Date(0, 0, 0, time.hour(), time.minute())).isBetween(
      new Date(0, 0, 0, 0, 1),
      new Date(0, 0, 0, 23, 59),
      'minute',
      '[]',
    )
  );
};

type validateMoneyProps = Pick<PlanTermProps, 'hourlyWage' | 'totalTimes'>;

export const validateMoney = (data: validateMoneyProps, min: number = 0) =>
  parseInt(data.hourlyWage) >= min;
